import React, { useEffect, useState } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { MdAdd, MdDescription } from 'react-icons/md';
import { StatCards } from '_atoms';
import { setSelectedIncident } from '_redux/slices/incidents/incidents';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { subscribeToIncidents } from '_services/incident-service';
import { Colors } from '_styles';

//#region styles — matches custom-reports-table.js (Allocation Report)
const containerStyle = {
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: '600px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    marginTop: 16,
};

const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    fontSize: '14px',
    fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle = {
    backgroundColor: '#f8fafc',
    color: '#374151',
    fontWeight: '600',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '2px solid #e5e7eb',
    borderRight: '1px solid #e5e7eb',
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    position: 'sticky',
    top: 0,
    zIndex: 2,
};

const cellStyle = {
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
    borderTop: '1px solid #f3f4f6',
    color: '#374151',
    fontSize: '14px',
};

const emptyStateStyle = {
    textAlign: 'center',
    padding: '56px 20px',
    color: '#6b7280',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
};

const emptyIconWrap = {
    width: 64,
    height: 64,
    borderRadius: '50%',
    background: '#eef5fd',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 14,
};

const addReportButton = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    padding: '10px 22px',
    border: 'none',
    borderRadius: 8,
    backgroundColor: Colors.PRM_LIGHT_BLUE,
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(48, 127, 226, 0.3)',
};

const filterBarStyle = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 4,
};

const chipStyle = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 20,
    border: `1px solid ${active ? Colors.PRM_LIGHT_BLUE : '#d1d5db'}`,
    backgroundColor: active ? Colors.PRM_LIGHT_BLUE : '#fff',
    color: active ? '#fff' : '#374151',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'all 0.15s ease',
});

const chipCountStyle = (active) => ({
    fontSize: 12,
    fontWeight: 700,
    padding: '1px 7px',
    borderRadius: 10,
    backgroundColor: active ? 'rgba(255,255,255,0.25)' : '#eef2f7',
    color: active ? '#fff' : '#6b7280',
});

const paginationBarStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 12,
    flexWrap: 'wrap',
};

const pageButtonStyle = (disabled) => ({
    padding: '6px 14px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    backgroundColor: disabled ? '#f3f4f6' : '#fff',
    color: disabled ? '#9ca3af' : '#374151',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
});

//#endregion

const STATUS_STYLES = {
    open: { background: '#E5E7EB', color: '#374151', label: 'Awaiting Acknowledgement' },
    in_progress: { background: '#DBEAFE', color: '#1E40AF', label: 'Acknowledged — In Progress' },
    fully_acknowledged: { background: '#FEF3C7', color: '#92400E', label: 'Acknowledged — Pending Approval' },
    returned: { background: '#FEE2E2', color: '#991B1B', label: 'Returned to Agent' },
    closed: { background: '#D1FAE5', color: '#065F46', label: 'Approved & Complete' },
};

const StatusBadge = ({ status }) => {
    const s = STATUS_STYLES[status] || STATUS_STYLES.open;
    return (
        <span style={{
            background: s.background,
            color: s.color,
            padding: '3px 10px',
            borderRadius: 4,
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: 'nowrap',
        }}>
            {s.label}
        </span>
    );
};

// Handles Firestore Timestamp objects (.toDate()) and the raw { seconds, nanoseconds } shape.
const toJsDate = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') return ts.toDate();
    if (typeof ts.seconds === 'number') return new Date(ts.seconds * 1000);
    const d = new Date(ts);
    return isNaN(d) ? null : d;
};

const formatDate = (timestamp) => {
    const d = toJsDate(timestamp);
    return d ? d.toLocaleDateString() : '—';
};



const HEADERS = ['Title', 'Type', 'Date', 'Status', 'Acknowledged', 'Flight'];

// Table filter chips — 'all' plus one per incident status.
const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Awaiting Acknowledgement' },
    { key: 'in_progress', label: 'In Progress' },
    { key: 'fully_acknowledged', label: 'Awaiting Approval' },
    { key: 'returned', label: 'Returned' },
    { key: 'closed', label: 'Completed' },
];

const PAGE_SIZE = 10;

const IncidentList = ({ onSelect, onAddReport }) => {
    const dispatch = useDispatch();
    const dbUser = useSelector(selectFirebaseInfo);
    const [incidents, setIncidents] = useState([]);
    const [hoveredRow, setHoveredRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [page, setPage] = useState(1);

    const iata = dbUser?.details?.authority;

    useEffect(() => {
        if (!iata) return;
        setLoading(true);
        const unsubscribe = subscribeToIncidents(iata, (data) => {
            setIncidents(data);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [iata]);

    const handleRowClick = (incident) => {
        dispatch(setSelectedIncident(incident));
        onSelect?.(incident);
    };

    const changeFilter = (key) => {
        setStatusFilter(key);
        setPage(1); // A new filter resets to the first page.
    };

    const totalIncidents = incidents.length;
    const awaitingAck = incidents.filter((i) => i.status === 'open').length;
    const inProgress = incidents.filter((i) => i.status === 'in_progress').length;
    const awaitingApproval = incidents.filter((i) => i.status === 'fully_acknowledged').length;

    // Per-status counts drive both the filter-chip badges and the stat cards.
    const statusCounts = incidents.reduce((acc, i) => {
        acc[i.status] = (acc[i.status] ?? 0) + 1;
        return acc;
    }, {});

    const statCards = [
        {
            title: 'Total Incidents',
            value: totalIncidents,
            subtitle: 'All incident reports on record',
            color: '#3b82f6',
        },
        {
            title: 'Awaiting Acknowledgement',
            value: awaitingAck,
            subtitle: 'Open — employees yet to respond',
            color: awaitingAck > 0 ? '#6b7280' : '#10b981',
        },
        {
            title: 'In Progress',
            value: inProgress,
            subtitle: 'Acknowledged — being dealt with',
            color: inProgress > 0 ? '#2563eb' : '#10b981',
        },
        {
            title: 'Awaiting Your Approval',
            value: awaitingApproval,
            subtitle: 'Employees acknowledged — action required',
            color: awaitingApproval > 0 ? '#f59e0b' : '#10b981',
        },
    ];

    // Apply the active status filter, then page the result.
    const filteredIncidents = statusFilter === 'all'
        ? incidents
        : incidents.filter((i) => i.status === statusFilter);

    const totalPages = Math.max(1, Math.ceil(filteredIncidents.length / PAGE_SIZE));
    const currentPage = Math.min(page, totalPages); // Clamp when the list shrinks.
    const pageStart = (currentPage - 1) * PAGE_SIZE;
    const pagedIncidents = filteredIncidents.slice(pageStart, pageStart + PAGE_SIZE);

    if (loading) {
        return (
            <Row style={{ marginTop: 20 }}>
                <Col>
                    <div style={emptyStateStyle}>
                        <Spinner animation="border" role="status" style={{ color: Colors.PRM_LIGHT_BLUE }} />
                        <p style={{ margin: '12px 0 0', fontSize: 14 }}>Loading incident reports…</p>
                    </div>
                </Col>
            </Row>
        );
    }

    return (
        <>
            {/* Stat cards */}
            <Row style={{ marginTop: 20, marginBottom: 20 }}>
                <Col>
                    <StatCards cards={statCards} />
                </Col>
            </Row>

            {/* Status filter chips */}
            {incidents.length > 0 && (
                <Row style={{ marginBottom: 12 }}>
                    <Col>
                        <div style={filterBarStyle}>
                            {FILTERS.map((f) => {
                                const active = statusFilter === f.key;
                                const count = f.key === 'all' ? incidents.length : (statusCounts[f.key] ?? 0);
                                return (
                                    <button
                                        key={f.key}
                                        type="button"
                                        onClick={() => changeFilter(f.key)}
                                        style={chipStyle(active)}
                                    >
                                        {f.label}
                                        <span style={chipCountStyle(active)}>{count}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </Col>
                </Row>
            )}

            {/* Table */}
            <Row>
                <Col>
                    {incidents.length === 0 ? (
                        <div style={emptyStateStyle}>
                            <div style={emptyIconWrap}>
                                <MdDescription size={32} color={Colors.PRM_LIGHT_BLUE} />
                            </div>
                            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', margin: 0 }}>
                                No Incident Reports
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                                No incident reports have been created yet. Use the Report Form tab to create one.
                            </p>
                            {onAddReport && (
                                <button type="button" onClick={onAddReport} style={addReportButton}>
                                    <MdAdd size={18} />
                                    Add Report
                                </button>
                            )}
                        </div>
                    ) : filteredIncidents.length === 0 ? (
                        <div style={emptyStateStyle}>
                            <div style={emptyIconWrap}>
                                <MdDescription size={32} color={Colors.PRM_LIGHT_BLUE} />
                            </div>
                            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#374151', margin: 0 }}>
                                No Matching Reports
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: 14 }}>
                                No incident reports match this filter.
                            </p>
                        </div>
                    ) : (
                        <>
                        <div style={containerStyle}>
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        {HEADERS.map((h) => (
                                            <th key={h} style={headerStyle}>{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {pagedIncidents.map((incident, idx) => {
                                        const assignedCount = incident.assignedEmployees?.length ?? 0;
                                        const ackedCount = Object.values(incident.acknowledgements ?? {})
                                            .filter((a) => a.status === 'acknowledged').length;
                                        const isHovered = hoveredRow === idx;
                                        return (
                                            <tr
                                                key={incident.id}
                                                style={{
                                                    backgroundColor: isHovered ? '#f9fafb' : 'white',
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.1s ease',
                                                }}
                                                onMouseEnter={() => setHoveredRow(idx)}
                                                onMouseLeave={() => setHoveredRow(null)}
                                                onClick={() => handleRowClick(incident)}
                                            >
                                                <td style={{ ...cellStyle, fontWeight: 500 }}>{incident.title}</td>
                                                <td style={cellStyle}>{incident.incidentType || '—'}</td>
                                                <td style={cellStyle}>{formatDate(incident.date)}</td>
                                                <td style={cellStyle}><StatusBadge status={incident.status} /></td>
                                                <td style={cellStyle}>{ackedCount} / {assignedCount}</td>
                                                <td style={{ ...cellStyle, color: '#6b7280' }}>{incident.flightNumber || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div style={paginationBarStyle}>
                            <span style={{ fontSize: 13, color: '#6b7280' }}>
                                Showing {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, filteredIncidents.length)} of {filteredIncidents.length}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={currentPage <= 1}
                                    style={pageButtonStyle(currentPage <= 1)}
                                >
                                    ← Prev
                                </button>
                                <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
                                    Page {currentPage} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={currentPage >= totalPages}
                                    style={pageButtonStyle(currentPage >= totalPages)}
                                >
                                    Next →
                                </button>
                            </div>
                        </div>
                        </>
                    )}

                </Col>
            </Row>
        </>
    );
};

export default IncidentList;
